import { signal } from '@preact/signals-react';
import * as Sentry from '@sentry/react';
import { AuthClient, } from '@thechristophe/web-oidc-client';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { createContext, useContext, useEffect, useRef, useState, } from 'react';
import { AuthState, } from './AuthData.js';
import AuthError from './AuthError.js';
import { _startLoginRedirect } from './loginRedirect.js';
import { LOADING as LOADING_STATE } from './mockData.js';
export const AuthContext = createContext(Object.assign(Object.assign({}, LOADING_STATE), { _errorCallbacks: {} }));
export const useAuth = () => useContext(AuthContext);
export const auth = signal(Object.assign(Object.assign({}, LOADING_STATE), { _errorCallbacks: {} }));
const parseUserProfile = (oidcUser) => {
    // don't provide partial data for consistency
    const email = typeof oidcUser.email !== 'undefined' && typeof oidcUser.email_verified !== 'undefined'
        ? {
            email: oidcUser.email,
            emailVerified: oidcUser.email_verified,
        }
        : {
            email: undefined,
            emailVerified: undefined,
        };
    const profile = Object.assign(Object.assign(Object.assign({ id: oidcUser.sub, name: oidcUser.name }, (typeof oidcUser.given_name !== 'undefined' && {
        firstName: oidcUser.given_name,
    })), (typeof oidcUser.family_name !== 'undefined' && {
        lastName: oidcUser.family_name,
    })), { preferredUsername: oidcUser.preferred_username, picture: oidcUser.picture });
    const roles = typeof oidcUser.roles !== 'undefined' &&
        typeof oidcUser.permissions !== 'undefined' &&
        typeof oidcUser.system_roles !== 'undefined'
        ? {
            permissions: oidcUser.permissions,
            roles: oidcUser.roles,
            system_roles: oidcUser.system_roles,
        }
        : {
            permissions: undefined,
            roles: undefined,
            system_roles: undefined,
        };
    const vrchat = typeof oidcUser['vrchat:id'] !== 'undefined' &&
        typeof oidcUser['vrchat:displayName'] !== 'undefined' &&
        typeof oidcUser['vrchat:isAgeGatedInstancesJoinable'] !== 'undefined'
        ? {
            vrchatId: oidcUser['vrchat:id'],
            vrchatDisplayName: oidcUser['vrchat:displayName'],
            vrchatIsAgeGatedInstancesJoinable: oidcUser['vrchat:isAgeGatedInstancesJoinable'],
        }
        : {
            vrchatId: undefined,
            vrchatDisplayName: undefined,
            vrchatIsAgeGatedInstancesJoinable: undefined,
        };
    return Object.assign(Object.assign(Object.assign(Object.assign({}, profile), email), roles), vrchat);
};
const AuthProvider = ({ configuration, errorCallbacks, routing, children, }) => {
    const wrappedLogin = ({ skipOrigin = false } = {}) => {
        if (routing && !skipOrigin) {
            const path = window.location.pathname;
            _startLoginRedirect(path.startsWith(routing.basePath) ? path.replace(routing.basePath, '') : path);
        }
        authClient.current.login();
    };
    const callbacks = {
        login: wrappedLogin,
        logout: () => authClient.current.logout(),
        renew: () => authClient.current.renew(),
        _errorCallbacks: Object.assign({ 401: () => {
                authClient.current.renew();
                return 'handled';
            } }, errorCallbacks),
    };
    // callbacks only read authClient.current when invoked, not during render
    // eslint-disable-next-line react-hooks/refs
    const [contextData, setContextData] = useState(Object.assign({ state: AuthState.Loading, user: undefined, auth: undefined }, callbacks));
    const authClient = useRef(new AuthClient(configuration, (newStatus) => {
        switch (newStatus.status) {
            case 'loading':
                {
                    const data = Object.assign({ state: AuthState.Loading, user: undefined, auth: undefined }, callbacks);
                    setContextData(data);
                    // Signals are mutable, so we can set the value directly
                    // eslint-disable-next-line react-hooks/immutability
                    auth.value = data;
                    Sentry.setUser({
                        id: undefined,
                    });
                }
                break;
            case 'not-logged-in':
                {
                    const data = Object.assign({ state: AuthState.NotLoggedIn, user: undefined, auth: undefined }, callbacks);
                    setContextData(data);
                    // eslint-disable-next-line react-hooks/immutability
                    auth.value = data;
                    Sentry.setUser({
                        id: undefined,
                    });
                }
                break;
            case 'error':
                {
                    const data = Object.assign({ state: AuthState.Error, user: undefined, auth: undefined, error: new AuthError(newStatus.error) }, callbacks);
                    setContextData(data);
                    // eslint-disable-next-line react-hooks/immutability
                    auth.value = data;
                    Sentry.setUser({
                        id: undefined,
                    });
                }
                break;
            case 'logged-in':
                {
                    const oidcUser = parseUserProfile(newStatus.user);
                    const data = Object.assign({ state: AuthState.LoggedIn, user: oidcUser, auth: {
                            token: newStatus.auth.token,
                        } }, callbacks);
                    setContextData(data);
                    // eslint-disable-next-line react-hooks/immutability
                    auth.value = data;
                    Sentry.setUser({
                        id: oidcUser.id,
                    });
                }
                break;
        }
    }));
    useEffect(() => authClient.current.browserInit(), []);
    return React.createElement(AuthContext.Provider, { value: contextData }, children);
};
export default AuthProvider;
//# sourceMappingURL=AuthProvider.js.map